import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./motion";
import { useI18n } from "../i18n";

// Shared layout primitives so every page keeps the same rhythm and widths.

export const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-6xl px-5 sm:px-8 2xl:max-w-7xl ${className}`}>{children}</div>
);

export const Eyebrow = ({ children, className = "", dark = false }) => (
  <p
    className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] ${
      dark ? "text-[#9db3af]" : "text-ink-3"
    } ${className}`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-[#6fc4b9]" : "bg-brand"}`} />
    {children}
  </p>
);

const variants = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  dark: "btn-dark",
  light: "btn-light",
  ghostLight: "btn-ghost-light",
};

export const buttonClass = (variant = "primary", size = "") =>
  `btn ${variants[variant] || variants.primary} ${size ? `btn-${size}` : ""}`;

export const Arrow = ({ size = 16 }) => (
  <ArrowRight
    size={size}
    className="transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1"
  />
);

export const ButtonLink = ({ to, variant = "primary", size = "", arrow = false, className = "", children, ...rest }) => (
  <Link to={to} className={`group ${buttonClass(variant, size)} ${className}`} {...rest}>
    {children}
    {arrow && <Arrow />}
  </Link>
);

// Page intro used on inner pages: eyebrow, headline, short lede, with a soft animated backdrop.
export const PageHeader = ({ eyebrow, title, lede, children }) => (
  <section className="relative overflow-hidden border-b border-line pt-36 pb-16 md:pt-44 md:pb-20">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="float absolute -top-32 end-[-10%] h-80 w-80 rounded-full bg-brand/10 blur-3xl" />
      <div
        className="float absolute -bottom-40 start-[-5%] h-72 w-72 rounded-full bg-brand/5 blur-3xl"
        style={{ "--delay": "-3s" }}
      />
    </div>
    <Container className="relative">
      {eyebrow && (
        <div className="rise">
          <Eyebrow>{eyebrow}</Eyebrow>
        </div>
      )}
      <h1
        className="rise mt-5 max-w-3xl font-display text-5xl leading-[1.05] tracking-[-0.01em] text-ink md:text-6xl lg:text-7xl"
        style={{ "--delay": "80ms" }}
      >
        {title}
      </h1>
      {lede && (
        <p className="rise mt-6 max-w-xl text-lg leading-relaxed text-ink-2" style={{ "--delay": "180ms" }}>
          {lede}
        </p>
      )}
      {children && (
        <div className="rise" style={{ "--delay": "280ms" }}>
          {children}
        </div>
      )}
    </Container>
  </section>
);

export const SectionHeading = ({ label, title, intro, dark = false, center = false, action }) => (
  <Reveal
    className={`mb-12 md:mb-16 ${center ? "mx-auto max-w-2xl text-center" : "flex flex-wrap items-end justify-between gap-6"}`}
  >
    <div className={center ? "" : "max-w-2xl"}>
      {label && <Eyebrow dark={dark}>{label}</Eyebrow>}
      <h2 className={`mt-4 font-display text-4xl leading-[1.08] md:text-5xl lg:text-6xl ${dark ? "text-white" : "text-ink"}`}>
        {title}
      </h2>
      {intro && <p className={`mt-4 text-lg leading-relaxed ${dark ? "text-[#b8c9c6]" : "text-ink-2"}`}>{intro}</p>}
    </div>
    {action}
  </Reveal>
);

export const Section = ({ className = "", children, id }) => (
  <section id={id} className={`py-20 md:py-28 2xl:py-36 ${className}`}>
    <Container>{children}</Container>
  </section>
);

// Closing call to action: every page ends here, pointing to Contact.
export const CtaBand = ({ secondary }) => {
  const { t } = useI18n();
  const second = secondary || { to: "/services", label: t("cta.secondary") };
  return (
    <section className="px-3 pb-3 sm:px-5 sm:pb-5">
      <div className="relative overflow-hidden rounded-[2rem] bg-deep py-20 md:py-28">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="float absolute -top-24 start-1/4 h-72 w-72 rounded-full bg-[#2e8b83]/40 blur-3xl" />
          <div
            className="float absolute -bottom-32 end-10 h-80 w-80 rounded-full bg-[#6fc4b9]/20 blur-3xl"
            style={{ "--delay": "-2s" }}
          />
          <svg className="absolute inset-0 h-full w-full opacity-[0.07]">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M40 0H0V40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>
        <Container className="relative text-center">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-4xl leading-[1.08] text-white md:text-6xl">{t("cta.title")}</h2>
            <p className="mx-auto mt-5 max-w-lg text-lg text-[#b8c9c6]">{t("cta.body")}</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <ButtonLink to="/contact" variant="light" size="lg" arrow>
                {t("cta.primary")}
              </ButtonLink>
              <ButtonLink to={second.to} variant="ghostLight" size="lg">
                {second.label}
              </ButtonLink>
            </div>
          </Reveal>
        </Container>
      </div>
    </section>
  );
};
